import { all, call, put, fork, takeEvery, select } from 'redux-saga/effects';
import { fetchOrgsSuccess, fetchOrgSuccess, showOrgMessage, onSaveOrgSuccess, onOrgDashboardSuccess, fetchOrgStatisticsSuccess } from 'actions/Organisation';
import { ON_SAVE_ORG, FETCH_ALL_ORG, FETCH_ORG, FETCH_ORGS_SELECT_ORG, ON_ORG_DASHBOARD, FETCH_ORG_STATISTICS } from 'constants/ActionTypes';
import { update, post } from 'services/ApiClient';
import { endpoint } from 'services/ApiConfig';
import { fetchDataRequest } from 'services/ApiService';
import { extractTokenId } from 'services/Authentication';
import { getActiveSurveys, getInactiveSurveys, extractOrgSurveySimpleChartData, prepareOrgSurveyAnswerSagaCall } from 'services/SurveyService';
import { objectStateComparator, auditLogHeaderEntry } from 'services/auditService';
import { isEqual } from 'lodash';
import { DATA_NOT_CHANGED_SO_NOT_UPDATED } from 'constants/messages';
import { ORGANISATION_STATISTICS, ADD_ORGANISATION, UPDATE_ORGANISATION } from 'constants/api';

const fetchOrganisationsRequest = async () => {
    const organisations = await fetchDataRequest('organisations');
    return organisations;
};

export function* fetchOrganisations() {
    try {
        const fetchedOrganisations = yield call(fetchOrganisationsRequest) || [];
        yield put(fetchOrgsSuccess(fetchedOrganisations));
    } catch (error) {
        yield put(showOrgMessage(error.messages));
    }
}

const fetchStatisticsRequest = async (selectedOrgId) => {
    const statistics = await fetchDataRequest(ORGANISATION_STATISTICS, ['orgid'], [selectedOrgId]);
    return statistics;
};

export function* fetchStatistics({ payload }) {
    try {
        const fetchedStatistics = yield call(fetchStatisticsRequest, payload) || {};
        yield put(fetchOrgStatisticsSuccess(fetchedStatistics));
    } catch (error) {
        yield put(showOrgMessage(error.messages));
    }
}

const fetchOrganisationRequest = async (orgid) => {
    // TODO do not have a get organisation api but not many orgs so get from the all orgs api call
    const orgs = await fetchOrganisationsRequest();
    if (orgs) {
        const filteredByOrgid = orgs.filter((org) => org.id === orgid);
        if (filteredByOrgid && filteredByOrgid.length > 0) {
            return filteredByOrgid[0];
        }
    }
    return {};
};

export function* fetchOrganisation({ payload }) {
    try {
        // payload will be the org id to fetch
        const fetchedOrganisation = yield call(fetchOrganisationRequest, payload) || {};
        yield put(fetchOrgSuccess(fetchedOrganisation));
    } catch (error) {
        yield put(showOrgMessage(error.messages));
    }
}

export function* fetchOrgsSelectOrg({ payload }) {
    // payload is the org id to select
    try {
        let fetchedOrganisation = {};
        const fetchedOrganisations = yield call(fetchOrganisationsRequest) || [];
        if (fetchedOrganisations) {
            const filteredByOrgid = fetchedOrganisations.filter((org) => org.id === payload);
            if (filteredByOrgid && filteredByOrgid.length > 0) {
                fetchedOrganisation = filteredByOrgid[0];
            }
        }
        yield put(fetchOrgsSuccess(fetchedOrganisations));
        yield put(fetchOrgSuccess(fetchedOrganisation));
    } catch (error) {
        yield put(showOrgMessage(error.messages));
    }
}

const currentOrg = (state) => state.orgs.selectedOrg;

export function* onOrganisationDashboard() {
    try {
        // payload will be the org id to fetch
        const selectedOrg = yield select(currentOrg);
        const inactiveSurveys = getInactiveSurveys(selectedOrg?.surveys);
        const activeSurveys = getActiveSurveys(selectedOrg?.surveys);
        let activeOrgSurveysSimpleChartData = [];
        let inactiveOrgSurveysSimpleChartData = [];

        if (activeSurveys?.length > 0) {
            const orgSurveyAnswerSagaCall = yield call(prepareOrgSurveyAnswerSagaCall, activeSurveys, selectedOrg?.id);
            const orgSurveyAnswers = orgSurveyAnswerSagaCall ? yield orgSurveyAnswerSagaCall : [];
            const simpleChartData = extractOrgSurveySimpleChartData(orgSurveyAnswers);
            // now add the other survey statistics for the reports/ graphs
            // TODO get real data for the graphs
            activeOrgSurveysSimpleChartData = simpleChartData.map((chartData) => {
                return {
                    surveyName: 'test',
                    surveyEndDate: '08/08/2020',
                    surveyStartDate: '07/07/2018',
                    noEmployeesInvited: 1,
                    noQuestionsCompleted: 194,
                    noQuestionsToComplete: 395,
                    simpleChartData: chartData?.data,
                    surveyActive: true,
                };
            });
        }

        if (inactiveSurveys?.length > 0) {
            const orgSurveyAnswerSagaCall = yield call(prepareOrgSurveyAnswerSagaCall, inactiveSurveys, selectedOrg?.id);
            const orgSurveyAnswers = orgSurveyAnswerSagaCall ? yield orgSurveyAnswerSagaCall : [];
            const simpleChartData = extractOrgSurveySimpleChartData(orgSurveyAnswers);

            // now add the other survey statistics for the reports/ graphs
            inactiveOrgSurveysSimpleChartData = simpleChartData.map((chartData) => {
                return {
                    surveyName: 'test',
                    surveyEndDate: '08/08/2020',
                    surveyStartDate: '07/07/2018',
                    noEmployeesInvited: 1,
                    noQuestionsCompleted: 194,
                    noQuestionsToComplete: 395,
                    simpleChartData: chartData?.data,
                    surveyActive: false,
                };
            });
        }

        // get the linechart data and the survey takeup data and present as one object for the dashboard to consume
        const orgDashboardData = { activeOrgSurveysSimpleChartData, inactiveOrgSurveysSimpleChartData };
        yield put(onOrgDashboardSuccess(orgDashboardData));
    } catch (error) {
        yield put(showOrgMessage(error.messages));
    }
}

const getOrgList = (state) => state.orgs.orgList;

export function* saveOrganisation({ payload }) {
    try {
        const allOrgs = yield select(getOrgList);
        const savedOrganisations = yield call(saveOrganisationRequest, allOrgs, payload);
        yield put(onSaveOrgSuccess(savedOrganisations));
    } catch (error) {
        if (typeof error === 'string') {
            yield put(showOrgMessage(error));
        } else {
            yield put(showOrgMessage(error.messages));
        }
    }
}

const saveOrganisationRequest = async (allOrgs, payload) => {
    try {
        let isNew = true;
        let currentOrgState = {};
        let updatedPayload = { ...payload };
        for (const org of allOrgs) {
            if (org.id === payload.id) {
                isNew = false;
                break;
            }
        }

        const endpointConfig = isNew ? endpoint(ADD_ORGANISATION) : endpoint(UPDATE_ORGANISATION);
        if (isNew) {
            // const _id = new Date().getUTCMilliseconds().toString().concat(new Date().getTime().toString()).toString();
            // eslint-disable-next-line no-unused-vars
            // const { password, ...noPassword } = payload
            const newPayload = { ...payload, password: 'testtest', registerorg: true }; // will be using passwordless so dummy password will work
            if (endpointConfig) {
                await post(endpointConfig.uri, newPayload)
                    .then((response) => {
                        // the response will return a token with the id so place it back into the local object
                        const id = response ? extractTokenId(response.token) : 'addOrgResponseError';
                        updatedPayload = { ...newPayload, id: id };
                        // orgList.push(updatedPayload);
                    })
                    .catch((error) => {
                        throw error;
                    });
            }
        } else {
            if (endpointConfig) {
                if (!isEqual(currentOrgState, payload)) {
                    const changedState = objectStateComparator([currentOrgState], [payload]);
                    await update(endpointConfig.uri, payload, auditLogHeaderEntry(changedState))
                        .then((payload) => payload) // already in orgList
                        .catch((error) => {
                            throw error;
                        });
                } else {
                    throw DATA_NOT_CHANGED_SO_NOT_UPDATED;
                }
            }
        }
        return updatedPayload;
    } catch (error) {
        throw error;
    }
};

export function* fetchOrganisationSaga() {
    yield takeEvery(FETCH_ORG, fetchOrganisation);
}

export function* fetchOrgsSelectOrgSaga() {
    yield takeEvery(FETCH_ORGS_SELECT_ORG, fetchOrgsSelectOrg);
}

export function* fetchOrganisationsSaga() {
    yield takeEvery(FETCH_ALL_ORG, fetchOrganisations);
}

export function* saveOrganisationSaga() {
    yield takeEvery(ON_SAVE_ORG, saveOrganisation);
}

export function* organisationDashboardSaga() {
    yield takeEvery(ON_ORG_DASHBOARD, onOrganisationDashboard);
}

export function* organisationStatisticsSaga() {
    yield takeEvery(FETCH_ORG_STATISTICS, fetchStatistics);
}

export default function* rootSaga() {
    yield all([
        fork(organisationStatisticsSaga),
        fork(fetchOrganisationsSaga),
        fork(saveOrganisationSaga),
        fork(fetchOrganisationSaga),
        fork(organisationDashboardSaga),
        fork(fetchOrgsSelectOrgSaga),
    ]);
}



// WEBPACK FOOTER //
// ./sagas/Organisation.js